import React, { useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { bindActionCreators } from 'redux'
import { v4 as uuidv4 } from 'uuid'
import {
  Box, Columns, Column, SubTitle,
  Divider, 
} from 'Components/common/bulma'
import { InputWithError as Input, SelectWithError as Select, } from 'Components/common/form'
import FileWithErrors from 'Components/common/form/FileLocal'
import DatePicker from 'Components/common/form/DateWithError'

import { addNotification } from 'Modules/principal'
import { ActionBar } from 'Components/common'
import Show from 'Components/common/Show'
import useRemove from 'Hooks/useRemove'
import Loader from 'Components/common/page/loaderFloat'
import useUnexpectedStockOutflow from './useUnexpectedStockOutflow'
import ProductsOrServicesService from 'App/services/ProductsOrServicesService'
import AutocompleteWithError from 'Components/common/form/AutocompleteWithError'
import Attachments from 'Containers/paymentOrders/attachments'


const hasPermission = (permissions = {}, permission) => {
  return +permissions[permission] === 1
}

function UnexpectedStockOutflowDetailForm({
  match,
  permissions,
  pushNavigation,
  addNotification,
  addSuccessNotification,
}) {
  const [productsOrServices, setProductsOrServices] = useState([])
  const { unexpectedStockOutflowStatus, inventoryReasons, create, update, upload, deleteOutflow,
     currentStatus, form = {}, initialProductValue, state } = useUnexpectedStockOutflow({ idUnexpectedStockOutflow: match?.params?.id })
  const {
    formFields, setFormFields, onFormFieldChange,
    isValidForm, getFormValues, showErrors,
    files,
    setFiles,
   } = form

  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [Modal, setShowModal] = useRemove({
    title: 'Eliminar salida extraordinaria',
    message: '¿Desea eliminar la salida extraordinaria?',
    handleOnRemove: async (id) => {
      setIsLoading(true)
      await deleteOutflow(id)
      setIsLoading(false)
      pushNavigation('/inventarios/salidas-extraordinarias')
    },
  })
  const isEditable = useMemo(() => +currentStatus === 1, [currentStatus])

  const getPermissions = useCallback(() => ({
    canSave: (+currentStatus === 1),
    canDelete: ( formFields.id?.input.value && +currentStatus === 1),
  }), [currentStatus, permissions, formFields.id?.input.value])

  const handleOnCancel = () => {
    pushNavigation('/inventarios/salidas-extraordinarias')
  }

  const handleOnDelete = () => {
    setShowModal(formFields.id?.input.value)
  }

  const onSave = async () => {
    try {
      if (!isValidForm()) {
        const validationError = new Error('ValidationError')
        validationError.name = 'ValidationError'
        throw validationError
      }

      setIsLoading(true)
      const values = getFormValues()

      if (formFields.id?.input.value) {
        // Actualizar salida extraordinaria existente
        const result = await update(formFields.id.input.value, values)
        addNotification({
          type: 'success',
          message: 'Salida extraordinaria actualizada correctamente',
        })
        setIsLoading(false)
        pushNavigation(`/inventarios/salidas-extraordinarias/${result.id}`)
      } else {
        // Crear nueva salida extraordinaria
        const result = await create(values)
        addNotification({
          type: 'success',
          message: 'Salida extraordinaria creada correctamente',
        })
        setIsLoading(false)
        pushNavigation(`/inventarios/salidas-extraordinarias/${result.id}`)
      }

    } catch (error) {
      if (error.name === 'ValidationError') {
        addNotification({
          type: 'danger',
          message: 'Favor de validar los campos',
        })
      } else {
        addNotification({
          type: 'danger',
          message: error.message || 'Error al guardar la salida extraordinaria',
        })
      }
      setIsLoading(false)
    }
  }


  const onChangeProduct = async (query) => {
    try {
      const { data } = await ProductsOrServicesService.search(query);
      setProductsOrServices(data);
    } catch (error) {
      setProductsOrServices([])
    }
  }

  const onClearProduct = () => {
    setProductsOrServices([])
  }

  const onSelectProduct = (selected, product) => {
    onFormFieldChange(selected)
    setFormFields({
      identificador: product?.identificador,
      pedimentoAduana: product?.pedimentoAduana,
      unitario: product?.unitario,
    })
    onFormFieldChange({ target: { name: 'importe', value: +product?.unitario * +formFields.piezas.input.value } })
  }

  const renderSuggestionProduct = suggestion => (
    <div>
      {suggestion.identificador} - {suggestion.concepto} {suggestion.pedimentoAduana ? `- ${suggestion.pedimentoAduana}` : ''}
    </div>
  )

  const getSuggestionValueProduct = suggestion => `${suggestion.identificador} - ${suggestion.concepto} ${suggestion.pedimentoAduana ? `- ${suggestion.pedimentoAduana}` : ''}`

  const handleFileUpload = async (file) => {
    try {
      setIsUploading(true)
      const uploadedFile = await upload(file.content)
      setFiles([...files, {idx: uuidv4(), path: uploadedFile, basename: file.name, toMove: true, canDelete: true}])
      addNotification({
        type: 'success',
        message: 'Archivo subido correctamente'
      })
    } catch (error) {
      addNotification({
        type: 'danger',
        message: error.message || 'Error al subir el archivo'
      })
    } finally {
      setIsUploading(false)
    }
  }

  const onPiecesChange = (e) => {
    onFormFieldChange({ target: { name: 'piezas', value: e.target.value } })
    onFormFieldChange({ target: { name: 'importe', value: +e.target.value * +formFields.unitario.input.value } })
  }

  const deleteFile = (file) => {
    setFiles(files.filter(f => f.idx !== file.idx))
  }

  // if (state === 'errorFetchingGeneralData') {
  //   return <Loader />
  // }

  return (
    <Box style={{ position: 'relative' }}>
      <Modal />
      <Show canShow={isLoading || isUploading}>
        <Loader />
      </Show>

      <ActionBar
        onSave={onSave}
        onCancel={handleOnCancel}
        onDelete={handleOnDelete}
        basicRole={['Cinventarios']}
        permissions={getPermissions()}
      />

      <SubTitle className="has-text-centered" is="3">Salida Extraordinaria</SubTitle>

      <Columns className="is-multiline">
        <Column className="is-half">
          <Select
            {...formFields.estatus}
            onChange={onFormFieldChange}
            showErrors={showErrors}
            options={unexpectedStockOutflowStatus}
          />
        </Column>
        <Column className="is-half">
          <DatePicker
            {...{...formFields.fecha, input:{...formFields.fecha.input, disabled: !isEditable}}}
            onChange={onFormFieldChange}
            showErrors={showErrors}
          />
        </Column>
      </Columns>
      <Divider content="Selecciona un producto" />
      <Columns className="is-multiline">
        <Column className="is-full">
          <AutocompleteWithError
            {...{...formFields.idProducto, input:{...formFields.idProducto.input, disabled: !isEditable}}}
            onFetchRequested={onChangeProduct}
            showErrors={showErrors}
            suggestions={productsOrServices}
            property="concepto" 
            listKey="id"
            onChange={onSelectProduct}
            onClearRequested={onClearProduct}
            renderSuggestion={renderSuggestionProduct}
            getSuggestionValue={getSuggestionValueProduct}
            initialInputValue={initialProductValue}
          // disabled={isLoadingCategories}
          />
        </Column>
        <Column className="is-half">
          <Input
            {...formFields.identificador}
            onChange={onFormFieldChange}
            showErrors={showErrors}
          />
        </Column>
        <Column className="is-half">
          <Input
            {...formFields.pedimentoAduana}
            onChange={onFormFieldChange}
            showErrors={showErrors}
          />
        </Column>
      </Columns>
      <Divider content="Indica la salida al inventario" />
    <Columns className="is-multiline">
        <Column className="is-half">
          <Input
            {...{...formFields.piezas, input:{...formFields.piezas.input, disabled: !isEditable}}}
            onChange={onPiecesChange}
            showErrors={showErrors}
            pattern="number"

          />
        </Column>
        <Column className="is-half">
          <Input
            {...formFields.unitario}
            onChange={onFormFieldChange}
            showErrors={showErrors}
            pattern="number"
          />
        </Column>
        <Column className="is-half">
          <Input
            {...formFields.importe}
            onChange={onFormFieldChange}
            showErrors={showErrors}
            pattern="number"
          />
        </Column>
      </Columns>

      <Divider content="Justifica la salida al inventario" />

      <Columns className="is-multiline">
        <Column className="is-half">
          <Select
            {...{...formFields.motivoSalida, input:{...formFields.motivoSalida.input, disabled: !isEditable}}}
            options={inventoryReasons}
            onChange={onFormFieldChange}
            showErrors={showErrors}
            valueProperty="id"
            valueLabel="motivo"
          />
        </Column>
        <Column className="is-full">
          <Input
            {...{...formFields.detalleSalida, input:{...formFields.detalleSalida.input, disabled: !isEditable}}}
            onChange={onFormFieldChange}
            showErrors={showErrors}
          />
        </Column>
        <Column className="is-half">
          <FileWithErrors
            {...{...formFields.adjuntos, input:{...formFields.adjuntos.input, disabled: !isEditable}}}
            onChange={onFormFieldChange}
            showErrors={showErrors}
            disabled={isUploading}
            onUploadFile={handleFileUpload}
          />
        </Column>
        <Column className="is-full">
          <Input
            {...formFields.observaciones}
            onChange={onFormFieldChange}
            showErrors={showErrors}
          />
        </Column>
      </Columns>
      <Columns className="is-multiline">
        <Column className="is-full">
          <Attachments files={files} deleteFile={deleteFile} />
        </Column>
      </Columns>
    </Box>
  )
}

const mapStateToProps = ({ catalogos, principal }) => ({
  estatusPersona: catalogos.estatusPersona,
  permissions: principal.inventoryPermission,
})

const mapDispatchToProps = dispatch => bindActionCreators({
  pushNavigation: push,
  addNotification,
  //   addSuccessNotification,
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(UnexpectedStockOutflowDetailForm) 